import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';

export type ResourceState<T> =
  | { kind: 'loading' }
  | { kind: 'error'; error: Error }
  | { kind: 'forbidden' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: T };

const SIMULATED: Record<string, ResourceState<never>['kind']> = {
  carregando: 'loading', erro: 'error', vazio: 'empty', 'sem-permissao': 'forbidden',
};

/**
 * Estado de carregamento padronizado (IM-UI-006). `?estado=carregando|vazio|erro|sem-permissao`
 * simula o estado para homologação visual, sem alterar dados.
 *
 * IM-EXT-013-C01: `reload` mostra o carregamento (primeira carga, nova tentativa, troca de filtro). `refresh` é a
 * REVALIDAÇÃO SILENCIOSA para acompanhar trabalho em segundo plano: o conteúdo atual continua na tela (nada é trocado por
 * esqueleto, nada é desmontado, a rolagem não muda), só uma consulta por vez, resposta idêntica não re-renderiza e
 * erro transitório mantém o que já estava carregado. Antes, todo polling passava por `loading` e remontava a tela.
 */
export function useResource<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[], isEmpty: (d: T) => boolean = () => false) {
  const [state, setState] = useState<ResourceState<T>>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [params] = useSearchParams();
  const simulated = SIMULATED[params.get('estado') ?? ''];
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const loadRef = useRef(load);
  loadRef.current = load;
  const isEmptyRef = useRef(isEmpty);
  isEmptyRef.current = isEmpty;
  const last = useRef<string | null>(null);
  const generation = useRef(0);
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => {
    if (simulated) {
      setState(simulated === 'error' ? { kind: 'error', error: new Error('Falha simulada para homologação.') } : ({ kind: simulated } as ResourceState<T>));
      return;
    }
    const gen = ++generation.current;
    inFlight.current?.abort();
    inFlight.current = null;
    const ac = new AbortController();
    setState({ kind: 'loading' });
    last.current = null;
    load(ac.signal)
      .then((data) => {
        if (ac.signal.aborted || gen !== generation.current) return;
        last.current = JSON.stringify(data);
        setState(isEmpty(data) ? { kind: 'empty' } : { kind: 'ready', data });
      })
      .catch((error: Error) => {
        if (ac.signal.aborted) return;
        setState(error instanceof ApiError && error.forbidden ? { kind: 'forbidden' } : { kind: 'error', error });
      });
    return () => ac.abort();
  }, [...deps, nonce, simulated]);

  const refresh = useCallback(() => {
    if (inFlight.current || last.current === null) return; // uma por vez; nada a revalidar antes da 1ª carga
    const gen = generation.current;
    const ac = new AbortController();
    inFlight.current = ac;
    loadRef.current(ac.signal)
      .then((data) => {
        if (ac.signal.aborted || gen !== generation.current) return; // resposta atrasada de outra consulta não sobrescreve
        const json = JSON.stringify(data);
        if (json === last.current) return; // payload idêntico: nada muda na tela
        last.current = json;
        setState(isEmptyRef.current(data) ? { kind: 'empty' } : { kind: 'ready', data });
      })
      .catch(() => { /* transitório: mantém o que está na tela; a próxima revalidação tenta de novo */ })
      .finally(() => { if (inFlight.current === ac) inFlight.current = null; });
  }, []);
  useEffect(() => () => inFlight.current?.abort(), []);

  return { state, reload, refresh };
}
